<template>
  <section
    class="estimator-register"
    :data-testid="`estimator-register-${register.id}`"
  >
    <h5 v-if="showRegisterName" class="mb-2" data-testid="estimator-rate-name">
      {{ registerLabel }}
    </h5>

    <div v-if="showEarlierRead" class="estimator-row mb-2">
      <SoSelect
        :model-value="state.read1Id"
        :items="readOptions"
        label="Earlier read"
        :placeholder="readPlaceholder"
        type="simple"
        data-testid="estimator-read1-select"
        @update:model-value="selectRead('read1', $event)"
      />
      <SoNumberField
        :model-value="state.read1Value"
        label="Value"
        :precision="0"
        :step="1"
        data-testid="estimator-read1-value"
        @update:model-value="editRead('read1', { read1Value: $event })"
      />
      <SoDatePicker
        :model-value="state.read1Date"
        label="Date"
        data-testid="estimator-read1-date"
        @update:model-value="editRead('read1', { read1Date: $event })"
      />
    </div>

    <div class="estimator-row mb-2">
      <SoSelect
        :model-value="state.read2Id"
        :items="readOptions"
        :label="laterReadLabel"
        :placeholder="readPlaceholder"
        type="simple"
        data-testid="estimator-read2-select"
        @update:model-value="selectRead('read2', $event)"
      />
      <SoNumberField
        :model-value="state.read2Value"
        label="Value"
        :precision="0"
        :step="1"
        data-testid="estimator-read2-value"
        @update:model-value="editRead('read2', { read2Value: $event })"
      />
      <SoDatePicker
        :model-value="state.read2Date"
        label="Date"
        data-testid="estimator-read2-date"
        @update:model-value="editRead('read2', { read2Date: $event })"
      />
    </div>

    <div class="estimator-row mb-2">
      <SoNumberField
        v-if="isEacAqInput"
        :model-value="state.eacAq"
        :label="`${eacOrAq} (kWh/year)`"
        :precision="2"
        :step="1"
        data-testid="estimator-eac-aq"
        @update:model-value="patch({ eacAq: $event })"
      />
      <SoSelect
        v-if="showUnitSelector"
        :model-value="state.unit"
        :items="volumeUnitOptions"
        label="Volume unit"
        placeholder="Select the unit"
        type="simple"
        data-testid="estimator-volume-unit"
        @update:model-value="patch({ unit: $event })"
      />
    </div>

    <p
      v-if="showUnitAsText"
      class="text-caption mb-2"
      data-testid="estimator-volume-unit-text"
    >
      Register unit: {{ unitLabel }}
    </p>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoNumberField from "@/components/formControls/SoNumberField.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import {
    VOLUME_UNIT_LABELS,
    VolumeUnit,
  } from "@/models/meterReadEstimate/meterReadEstimate.types"
  import { formatDate } from "@/helpers/dateHelper"
  import {
    EstimatorUiMode,
    actualReadsNewestFirst,
    eacOrAqLabel,
    hasKnownVolumeUnit,
    usesTwoReads,
    volumeUnitFor,
  } from "./estimatorHelper"
  import {
    IRegisterFormState,
    READ_PLACEHOLDER,
    RegisterFormPatch,
    USER_INPUT,
  } from "./estimatorForm.types"

  export default defineComponent({
    components: { SoDatePicker, SoNumberField, SoSelect },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      register: {
        type: Object as PropType<IRegister>,
        required: true,
      },
      state: {
        type: Object as PropType<IRegisterFormState>,
        required: true,
      },
      mode: {
        type: String as PropType<EstimatorUiMode>,
        required: true,
      },
      showRegisterName: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["patch"],
    computed: {
      registerLabel(): string {
        return this.register.rateName ?? "Standard"
      },
      eacOrAq(): string {
        return eacOrAqLabel(this.meterpoint)
      },
      showEarlierRead(): boolean {
        return usesTwoReads(this.mode)
      },
      /**
       * With two reads the pair is earlier and later. With one, "known read" is
       * the clearer name - there is nothing for it to be later than.
       */
      laterReadLabel(): string {
        return usesTwoReads(this.mode) ? "Later read" : "Known read"
      },
      isEacAqInput(): boolean {
        return this.mode === EstimatorUiMode.ReadFromEacAq
      },
      isGas(): boolean {
        return !this.meterpoint.isElectricity
      },
      /** Only ask where the meter point does not already tell us (SO-32714). */
      showUnitSelector(): boolean {
        return this.isGas && !hasKnownVolumeUnit(this.register)
      },
      showUnitAsText(): boolean {
        return this.isGas && hasKnownVolumeUnit(this.register)
      },
      unitLabel(): string {
        const unit = volumeUnitFor(this.register)

        return unit ? VOLUME_UNIT_LABELS[unit] : ""
      },
      readPlaceholder(): string {
        return READ_PLACEHOLDER
      },
      actualReads() {
        return actualReadsNewestFirst(this.register)
      },
      /*
        Every actual read, not a window on them. A register on daily smart
        reads carries one a day, so the old 50-option cap reached back about six
        weeks - and a back-billing case needs months or years. VSelect renders
        its menu through VVirtualScroll, so only the visible rows are mounted
        however long the list is.
      */
      readOptions() {
        const reads = this.actualReads.map((read) => ({
          title: `${read.cumulative} on ${formatDate(read.date)} (${
            read.source
          })`,
          value: String(read.id),
        }))

        return [{ title: "Input a value", value: USER_INPUT }, ...reads]
      },
      volumeUnitOptions() {
        return Object.values(VolumeUnit).map((unit) => ({
          title: VOLUME_UNIT_LABELS[unit],
          value: unit,
        }))
      },
    },
    methods: {
      patch(patch: RegisterFormPatch) {
        this.$emit("patch", patch)
      },
      /**
       * Editing either half of a read - its value or its date - means the agent
       * is entering their own, so the dropdown moves to "Input a value" with
       * it. Both controls stay enabled, so a read picked from the list can
       * still be nudged, but the three can no longer disagree: the dropdown
       * used to go on naming a read whose value or date had been changed
       * underneath it.
       */
      editRead(field: "read1" | "read2", patch: RegisterFormPatch) {
        this.patch({ ...patch, [`${field}Id`]: USER_INPUT })
      },
      selectRead(field: "read1" | "read2", readId: string) {
        const read = this.actualReads.find(
          (candidate) => String(candidate.id) === readId,
        )

        this.patch({
          [`${field}Id`]: readId,
          [`${field}Value`]: read ? read.cumulative : null,
          [`${field}Date`]: read ? read.date.toDate() : null,
        })
      },
    },
  })
</script>

<style scoped>
  /* Dropdown, value and date share the row equally. */
  .estimator-row {
    display: flex;
    gap: 12px;
  }

  .estimator-row > * {
    flex: 1 1 0;
    min-width: 0;
  }
</style>
